import { exponentialBackoffMs } from '@developerz/kit';
import { QueryClient } from '@tanstack/solid-query';
import { isForbidden, isUnauthorized } from './api';

// One client per app. Data is fresh for 30s, then re-fetched on the next
// subscriber — and when the tab comes back into view (`refetchOnWindowFocus`).
//
// Focus refetch was OFF ("so the audit timeline doesn't churn"), and most
// entities have no live topic, so a tab backgrounded for an hour came back
// showing that hour's-old answer until something else happened to re-read it.
// It is the cheapest correct mechanism there is: TanStack's own focus manager
// already listens to `visibilitychange`, it re-reads only MOUNTED queries, and
// only STALE ones — tabbing around inside 30 s costs nothing. The audit timeline
// is an SSE append log, not a query, so it never churned on this at all. A live
// read (`lib/live/live-query.ts`) turns it off for itself while the account
// stream vouches for it, and back on the moment it does not.
//
// The 401 LATCH is not here. A dead credential has to be caught wherever a call
// is made — including the hand-rolled `apiFetch` callers that never become a
// query — so `apiFetch` sets it and `<App/>` reads it (lib/session-expiry.ts).
// What belongs here is the other half of that finding: a 401 must not be
// RETRIED. The credential is gone; the second attempt fails identically, and a
// screenful of panels each burning their retry turns one dead session into a
// burst of guaranteed-401 traffic at the API.
// WHETHER to retry is policy (above); HOW LONG to wait before the retry is not.
// TanStack's default backoff is a real 1s sleep, and every error-path test in the
// SPA renders through this same client — so the suite paid ~1s of wall per
// error assertion, tens of seconds across the dashboard, to prove nothing about
// the product. Under `bun test` (which sets NODE_ENV=test) the retry still
// happens — same call count, same ordering, same final state — it just fires on
// the next macrotask instead of a second later. `typeof process` is guarded
// because this module ships in a browser bundle.
const IN_TEST = typeof process !== 'undefined' && process.env['NODE_ENV'] === 'test';

/**
 * TanStack's own default backoff — 1s doubling to a 30s ceiling — restated so
 * the test override is visibly the odd one. The curve is `@developerz/kit`'s one
 * {@link exponentialBackoffMs} with those two bounds named; the numbers are
 * TanStack's, not the kit's defaults, so they stay spelled here.
 */
const retryDelay = (attempt: number): number =>
  IN_TEST ? 0 : exponentialBackoffMs(attempt, { baseMs: 1_000, capMs: 30_000 });

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // Was `retry: 1`. Same single retry for anything that might heal
        // (network blip, 500, 429) — none for a credential refusal. A 401 is a
        // dead credential and a 403 is a live one that may not; the retry
        // carries the same credential, the same scopes and the same tenant
        // claim, so both are refused identically. The 403 half arrived with the
        // denied state (slice 17): a screen that deliberately offers no Retry
        // while the client silently asked twice is not the no-retry it claims.
        retry: (failureCount: number, error: unknown) =>
          !isUnauthorized(error) && !isForbidden(error) && failureCount < 1,
        retryDelay,
        refetchOnWindowFocus: true,
      },
    },
  });
}
